
import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { User, Council } from '@/types';
import { apiService } from '@/services/api';
import { toast } from 'sonner';
import { getUserDisplayName } from '@/lib/utils';

function normalizeAuthUser(raw: unknown): User | null {
  if (!raw || typeof raw !== 'object') return null;
  const u = raw as Record<string, unknown>;
  const email = typeof u.email === 'string' ? u.email : '';
  const role = typeof u.role === 'string' ? u.role : 'employee';
  const id = u.id != null ? Number(u.id) : NaN;
  if (!Number.isFinite(id) && !email) return null;
  const username =
    (typeof u.username === 'string' && u.username.trim()) ||
    getUserDisplayName({
      name: typeof u.name === 'string' ? u.name : undefined,
      email,
    });
  const user: User = {
    id: Number.isFinite(id) ? id : 0,
    username,
    email,
    role,
  };
  if (typeof u.avatar === 'string') user.avatar = u.avatar;
  if (u.cid != null && u.cid !== '') user.cid = Number(u.cid);
  return user;
}

interface AuthContextType {
  user: User | null;
  council: Council | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [council, setCouncil] = useState<Council | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch council data when user or cid changes
  const fetchCouncil = async (cid: number | null | undefined) => {
    if (!cid) {
      setCouncil(null);
      return;
    }

    try {
      const response = await apiService.getCouncilByCode(cid);
      // handleResponse returns response.data, which is the backend response object
      // Backend returns { message, status, data: { code, name, path } }
      if (response) {
        // If response has data property, use it, otherwise use response directly
        const councilData = response.data || response;
        if (councilData && (councilData.code || councilData.name)) {
          setCouncil(councilData);
        }
      }
    } catch (error) {
      console.error('Error fetching council:', error);
      setCouncil(null);
    }
  };

  useEffect(() => {
    // Check if user is stored in localStorage
    const storedUser = localStorage.getItem('payroll_user');
    if (storedUser && storedUser !== 'undefined') {
      try {
        const parsedUser = normalizeAuthUser(JSON.parse(storedUser));
        if (parsedUser) {
          setUser(parsedUser);
          localStorage.setItem('payroll_user', JSON.stringify(parsedUser));
          if (parsedUser.cid) {
            fetchCouncil(parsedUser.cid);
          }
        }
      } catch {
        localStorage.removeItem('payroll_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setLoading(true);
      // Call backend API for login
      const res = await apiService.login(email, password);
      
      if (res && res.status == 200) {
        const normalized = normalizeAuthUser(res.data);
        if (!normalized) {
          toast.error('Invalid user data from server');
          return false;
        }
        setUser(normalized);
        localStorage.setItem('payroll_user', JSON.stringify(normalized));
        localStorage.setItem('auth_token', normalized.email || '');
        if (normalized.cid) {
          await fetchCouncil(normalized.cid);
        }
        toast.success('Login successful');
        return true;
      } else {
        toast.error('Invalid email or password');
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('An error occurred during login');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setCouncil(null);
    localStorage.removeItem('payroll_user');
    toast.success('Logged out successfully');
  };

  return (
    <AuthContext.Provider value={{ user, council, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
